import { useState, useCallback } from 'react';
import { Card, CardHeader, CardContent, Box, Typography, IconButton, Tooltip, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import { FileDownload as FileDownloadIcon, FilterAlt as FilterIcon, TrendingUp as TrendingUpIcon, PeopleOutlined as PeopleIcon } from '@mui/icons-material';
import { RegionData } from '@/types/dashboard';
import { fetchRegionDrillDown, DrillDownPolicy } from '@/services/api';
import { RegionDrillDownModal } from './RegionDrillDownModal';
import { ResponsiveChoropleth } from '@nivo/geo';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { format } from 'date-fns';
import geoData from './india-states.json';
import indiaTopoJSON from '../../../public/data/geography-data/india.topo.json';

// Use imported JSON file directly
const INDIA_TOPO_JSON = indiaTopoJSON;

// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5'
};

interface RegionalMapProps {
  regionData: Record<string, RegionData>;
  onRegionClick?: (stateCode: string) => void;
}

interface MapTooltipProps {
  region: string;
  data: RegionData;
}

const MapTooltip = ({ region, data }: MapTooltipProps) => {
  if (!data) return null;
  
  return (
    <Box
      sx={{
        padding: 2,
        background: '#ffffff',
        border: '1px solid #ccc',
        borderRadius: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {region}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Base:</Typography>
          <Typography variant="body2" color="text.primary">
            {(data.totalLeads || 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">Converted:</Typography>
          <Typography variant="body2" color="text.primary">
            {(data.converted || 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">Conversion %:</Typography>
          <Typography 
            variant="body2" 
            color={data.conversionRate >= 50 ? 'success.main' : 'error.main'}
          >
            {(data.conversionRate || 0).toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">Managers:</Typography>
          <Typography variant="body2" color="text.primary">
            {(data.managers?.length || 0)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const Legend = ({ viewBy }: { viewBy: 'leads' | 'conversion' }) => (
  <Paper
    elevation={2}
    sx={{
      position: 'absolute',
      bottom: 20,
      right: 20,
      p: 0,
      borderRadius: 1,
      zIndex: 1000,
      border: `1px solid ${CHOLA_COLORS.grey}`,
      backgroundColor: 'rgba(255, 255, 255, 0.95)'
    }}
  >
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ 
        mb: 1, 
        color: CHOLA_COLORS.primary,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5
      }}>
        <FilterIcon sx={{ fontSize: 18 }} />
        {viewBy === 'leads' ? 'Lead Contribution to NOP base' : 'NOP Conversion %'}
      </Typography>
      
      {viewBy === 'leads' ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: CHOLA_COLORS.grey, borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#F44336', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Low (&lt;5% of Total Base)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#FF6E00', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Medium (5-15% of Total Base)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#4CAF50', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>High (&gt;15% of Total Base)</Typography>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: CHOLA_COLORS.grey, borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#F44336', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Low (&lt;40% conversion)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#FF6E00', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Medium (&gt;40 to 60% conversion)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#4CAF50', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>High (&gt;60% conversion)</Typography>
          </Box>
        </>
      )}
    </Box>
  </Paper>
);

interface DrillDownData {
  id: string;
  policyNumber: string;
  product: string;
  channel: string;
  status: string;
  manager: {
    rm: string;
    zm: string;
    nsm: string;
  };
  agent: {
    code: string;
    name: string;
  };
  updateDate: string;
}

export function RegionalMap({ regionData, onRegionClick }: RegionalMapProps) {
  const [hoveredRegion, setHoveredRegion] = useState<{ code: string; name: string } | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [viewBy, setViewBy] = useState<'leads' | 'conversion'>('conversion');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: 'leads' | 'conversion' | null) => {
    if (newView !== null) {
      setViewBy(newView);
    }
  };

  // Add pointer animation keyframes
  const pointerAnimation = {
    '@keyframes pulse': {
      '0%': {
        transform: 'scale(1)',
        opacity: 0.7,
      },
      '50%': {
        transform: 'scale(1.1)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(1)',
        opacity: 0.7,
      },
    }
  };

  const getRegionColor = (regionId: string) => {
    if (!regionData[regionId]) return CHOLA_COLORS.grey;
    
    if (viewBy === 'leads') {
      const totalLeads = regionData[regionId].totalLeads;
      if (totalLeads === 0) return CHOLA_COLORS.grey;
      
      // Calculate percentage of Total Base
      const totalAllLeads = Object.values(regionData).reduce((sum, region) => sum + (region.totalLeads || 0), 0);
      const percentage = (totalLeads / totalAllLeads) * 100;
      
      if (percentage < 5) return '#F44336'; // Red for low share
      if (percentage < 15) return '#FF6E00'; // Orange for medium share
      return '#4CAF50'; // Green for high share
    } else {
      // Conversion rate view - using different colors as specified
      const conversionRate = regionData[regionId].conversionRate;
      if (conversionRate === 0) return CHOLA_COLORS.grey;
      
      if (conversionRate < 40) return '#F44336'; // Red for low conversion
      if (conversionRate < 60) return '#FF6E00'; // Orange for medium conversion
      return '#4CAF50'; // Green for high conversion
    }
  };

  const getRegionStyles = (stateCode: string) => {
    const isSelected = selectedRegion === stateCode;
    
    return {
      default: {
        fill: getRegionColor(stateCode),
        stroke: isSelected ? CHOLA_COLORS.secondary : (regionData[stateCode] ? CHOLA_COLORS.lightBlue : '#999'),
        strokeWidth: isSelected ? 3 : (regionData[stateCode] ? 1 : 0.5),
        strokeDasharray: isSelected ? '0' : 'none',
        outline: "none",
        cursor: regionData[stateCode] ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        // Add a subtle glow effect for selected region
        filter: isSelected ? 'drop-shadow(0 0 5px rgba(227, 24, 55, 0.8))' : 'none'
      },
      hover: {
        fill: regionData[stateCode] ? CHOLA_COLORS.lightBlue : getRegionColor(stateCode),
        stroke: isSelected ? CHOLA_COLORS.secondary : (regionData[stateCode] ? CHOLA_COLORS.primary : '#999'),
        strokeWidth: isSelected ? 3 : (regionData[stateCode] ? 2 : 0.5),
        outline: "none",
        filter: isSelected 
          ? 'drop-shadow(0 0 5px rgba(227, 24, 55, 0.8))' 
          : (regionData[stateCode] ? 'brightness(1.1)' : 'none'),
        transform: (regionData[stateCode] && !isSelected) ? 'translateY(-2px)' : 'none',
        transition: 'all 0.3s ease'
      },
      pressed: {
        fill: regionData[stateCode] ? CHOLA_COLORS.lightBlue : getRegionColor(stateCode),
        stroke: isSelected ? CHOLA_COLORS.secondary : (regionData[stateCode] ? CHOLA_COLORS.primary : '#999'),
        strokeWidth: isSelected ? 3 : (regionData[stateCode] ? 2 : 0.5),
        outline: "none"
      }
    };
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (hoveredRegion) {
      setTooltipPos({
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        height: '100%',
        position: 'relative',
        bgcolor: CHOLA_COLORS.background,
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...pointerAnimation
      }}
      onMouseMove={handleMouseMove}
    >
      {/* View toggle buttons */}
      <Box sx={{ 
        position: 'absolute', 
        top: 20, 
        right: 20, 
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <ToggleButtonGroup
          value={viewBy}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#475569',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: `${CHOLA_COLORS.primary}15`,
                color: CHOLA_COLORS.primary,
                '&:hover': {
                  backgroundColor: `${CHOLA_COLORS.primary}25`,
                },
              },
            },
            '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
              borderRadius: '4px 0 0 4px',
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderRadius: '0 4px 4px 0',
            },
          }}
        >
          <ToggleButton value="conversion">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon fontSize="small" />
              <Typography variant="caption">NOP Conversion</Typography>
            </Box>
          </ToggleButton>
          {/* <ToggleButton value="leads">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon fontSize="small" />
              <Typography variant="caption">Leads</Typography>
            </Box>
          </ToggleButton> */}
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ 
        height: '98%', 
        width: '98%', 
        position: 'relative'
      }}>
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 1130,
            center: [78.9629, 22.5937]
          }}
          style={{
            width: "100%",
            height: "100%"
          }}
        >
          <Geographies geography={INDIA_TOPO_JSON}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateCode = geo.id;
                const stateName = geo.properties.name;
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={(evt) => {
                      setHoveredRegion({ 
                        code: stateCode,
                        name: stateName
                      });
                      setTooltipPos({ 
                        x: evt.clientX,
                        y: evt.clientY
                      });
                    }}
                    onMouseLeave={() => {
                      setHoveredRegion(null);
                      setTooltipPos(null);
                    }}
                    onClick={() => {
                      if (onRegionClick && regionData[stateCode]) {
                        onRegionClick(stateCode);
                        setSelectedRegion(stateCode);
                      }
                    }}
                    style={getRegionStyles(stateCode)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
        
        <Legend viewBy={viewBy} />

        {/* Add instructional pointer */}
        {/* <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            pointerEvents: 'none',
            animation: 'pulse 2s infinite ease-in-out',
            zIndex: 1000
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: `${CHOLA_COLORS.primary}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: CHOLA_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px'
              }}
            >
              👆
            </Box>
          </Box>
          <Paper
            elevation={3}
            sx={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '8px',
              border: `2px solid ${CHOLA_COLORS.primary}`,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: CHOLA_COLORS.primary,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              Click on any region to view detailed analysis
            </Typography>
          </Paper>
        </Box> */}

        {hoveredRegion && tooltipPos && (
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              top: tooltipPos.y + 20,
              left: tooltipPos.x + 10,
              p: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              borderRadius: 2,
              minWidth: 220,
              border: `2px solid ${CHOLA_COLORS.primary}`,
              zIndex: 1000,
              pointerEvents: 'none',
              transform: 'translate(-50%, 0)',
              transition: 'all 0.1s ease',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                border: '8px solid transparent',
                borderBottomColor: CHOLA_COLORS.primary,
                zIndex: 1001
              }
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 1.5, 
                color: CHOLA_COLORS.primary, 
                fontWeight: 600,
                borderBottom: `1px solid ${CHOLA_COLORS.primary}20`,
                pb: 1
              }}
            >
              {hoveredRegion.name}
            </Typography>
            {regionData[hoveredRegion.code] ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Base NOP:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {regionData[hoveredRegion.code].totalLeads?.toLocaleString() || '0'} 
                    {(() => {
                      const totalAllLeads = Object.values(regionData).reduce((sum, region) => sum + (region.totalLeads || 0), 0);
                      const percentage = ((regionData[hoveredRegion.code].totalLeads || 0) / totalAllLeads * 100);
                      return ` (${percentage.toFixed(1)}%)`;
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Renewed NOP:</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {regionData[hoveredRegion.code].converted?.toLocaleString() || '0'} 
                    ({(regionData[hoveredRegion.code].conversionRate || 0).toFixed(1)}%)
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No data available
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
} 